import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * A right-hand slot on the wizard step header line (the title + subtitle
 * block): step content may place ONE small control there — e.g. the Smart
 * BOM pill on the discovery sub-step — so it aligns with the header text
 * instead of costing the page an extra row. The slot clears itself when the
 * contributing component unmounts, so a step can never leak chrome into the
 * next one.
 */
interface HeaderAsideContextValue {
  node: React.ReactNode;
  setNode: (node: React.ReactNode) => void;
}

const HeaderAsideContext = createContext<HeaderAsideContextValue | null>(null);

export const WizardHeaderAsideProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [node, setNodeState] = useState<React.ReactNode>(null);
  const setNode = useCallback((n: React.ReactNode) => setNodeState(n), []);
  return (
    <HeaderAsideContext.Provider value={{ node, setNode }}>{children}</HeaderAsideContext.Provider>
  );
};

/** Setter for contributors — call inside an effect and clear on unmount. */
export const useSetWizardHeaderAside = (): ((node: React.ReactNode) => void) => {
  const ctx = useContext(HeaderAsideContext);
  return ctx ? ctx.setNode : () => undefined;
};

/** Render target — placed by RFQWizard beside the step title block. */
export const WizardHeaderAsideOutlet: React.FC<{ className?: string }> = ({ className = '' }) => {
  const ctx = useContext(HeaderAsideContext);
  if (!ctx?.node) return null;
  return <div className={`shrink-0 ${className}`}>{ctx.node}</div>;
};
